import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  type OnDestroy,
} from '@angular/core';
import { ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { ContactNumberControls } from '@principle-theorem/ng-principle-shared';
import {
  InputSearchFilter,
  MOMENT_DATEPICKER_PROVIDERS,
  TrackByFunctions,
  TypedFormArray,
  TypedFormControl,
  TypedFormGroup,
  toSearchStream,
} from '@principle-theorem/ng-shared';
import {
  Address,
  NameHelpers,
  ReferralSource,
} from '@principle-theorem/principle-core';
import {
  GENDERS,
  IContactNumber,
  IDVACard,
  IHealthFundCard,
  IMedicareCard,
  IMohSchool,
  IPatient,
  IReferralSource,
  IReferralSourceConfiguration,
  isMohSchool,
  NZ_SCHOOLS,
  PatientHealthCardType,
  PatientOptionalField,
  type Gender,
} from '@principle-theorem/principle-core/interfaces';
import { isISODateType, Region, type WithRef } from '@principle-theorem/shared';
import { omit } from 'lodash';
import * as moment from 'moment-timezone';
import { Observable, ReplaySubject, Subject, of } from 'rxjs';
import { debounceTime, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { PatientDetailsFormDataGroup } from './patient-details-form-group';

@Component({
  selector: 'pr-patient-details-form',
  templateUrl: './patient-details-form.component.html',
  styleUrls: ['./patient-details-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  standalone: false,
})
export class PatientDetailsFormComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _dobBlur$ = new Subject<void>();
  private _currentPatient: IPatient | undefined;
  trackByIndex = TrackByFunctions.index<TypedFormGroup<IContactNumber>>();
  trackByGender = TrackByFunctions.variable<Gender>();
  trackByReferralSource =
    TrackByFunctions.ref<WithRef<IReferralSourceConfiguration>>();
  patientDetails$ = new ReplaySubject<IPatient>(1);
  referralSources$ = new ReplaySubject<WithRef<IReferralSourceConfiguration>[]>(
    1
  );
  requiredFields$ = new ReplaySubject<PatientOptionalField[]>(1);
  healthCardTypes$ = new ReplaySubject<PatientHealthCardType[]>(1);
  hasHealthCards$: Observable<boolean>;
  showSchoolField$: Observable<boolean>;
  form = new PatientDetailsFormDataGroup();
  genders = GENDERS;
  today = moment();
  contactNumberControls: ContactNumberControls;
  Region = Region;
  errorStateMatcher = new ShowOnDirtyErrorStateMatcher();
  schoolSearch: InputSearchFilter<IMohSchool>;

  @Input()
  set healthCardTypes(healthCardTypes: PatientHealthCardType[]) {
    if (healthCardTypes) {
      this.healthCardTypes$.next(healthCardTypes);
    }
  }

  @Input()
  set referralSources(
    referralSources: WithRef<IReferralSourceConfiguration>[]
  ) {
    if (referralSources) {
      this.referralSources$.next(referralSources);
    }
  }

  @Input()
  set requiredFields(requiredFields: PatientOptionalField[]) {
    if (requiredFields) {
      this.requiredFields$.next(requiredFields);
    }
  }

  @Input()
  set patientDetails(patientDetails: IPatient) {
    if (patientDetails) {
      this.patientDetails$.next(patientDetails);
    }
  }

  @Output() patientDetailsChange = new EventEmitter<Partial<IPatient>>();

  constructor() {
    this.contactNumberControls = new ContactNumberControls(
      this.form.controls.contactNumbers as TypedFormArray<IContactNumber>
    );
    this.patientDetails$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((patient) => {
        this._currentPatient = patient;
        this.contactNumberControls.initContactNumberControls(patient, {
          emitEvent: false,
        });

        this.form.patchValue(
          {
            ...patient,
            address: Address.normaliseAddress(patient),
            firstName: NameHelpers.firstName(patient.name),
            lastName: NameHelpers.lastName(patient.name),
            school: patient.metadata?.moh?.school,
          },
          { emitEvent: false }
        );
        this._dobBlur$.next();
      });
    this.hasHealthCards$ = this.healthCardTypes$.pipe(
      map((healthCardTypes) => healthCardTypes.length > 0)
    );

    const dobCtrl = this.form.controls.dateOfBirth;
    this.showSchoolField$ = this._dobBlur$.pipe(
      startWith(undefined),
      map(() => this._isUnder18(dobCtrl.value)),
      tap((show) => {
        if (!show) {
          this.form.controls.school.reset(undefined, { emitEvent: false });
        }
      })
    );

    const schoolSearchStream = toSearchStream(
      this.form.controls.school as TypedFormControl<IMohSchool>
    );
    this.schoolSearch = new InputSearchFilter<IMohSchool>(
      of(NZ_SCHOOLS),
      schoolSearchStream,
      ['schoolName']
    );

    this.form.valueChanges
      .pipe(debounceTime(500), takeUntil(this._onDestroy$))
      .subscribe(() => this.submit());
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  onDobBlur(): void {
    this._dobBlur$.next();
  }

  get healthFundCard(): TypedFormGroup<IHealthFundCard> {
    return this.form.controls.healthFundCard as TypedFormGroup<IHealthFundCard>;
  }

  get medicareCard(): TypedFormGroup<IMedicareCard> {
    return this.form.controls.medicareCard as TypedFormGroup<IMedicareCard>;
  }

  get dvaCard(): TypedFormGroup<IDVACard> {
    return this.form.controls.dvaCard as TypedFormGroup<IDVACard>;
  }

  healthCardEnabled$(type: string): Observable<boolean> {
    return this.healthCardTypes$.pipe(
      map((healthCardTypes) =>
        healthCardTypes.includes(type as PatientHealthCardType)
      )
    );
  }

  submit(): void {
    if (this.form.valid) {
      const data = this.form.value;
      this.patientDetailsChange.emit({
        ...omit(data, ['firstName', 'lastName', 'school']),
        name: `${data.firstName} ${data.lastName}`,
        address: data.address?.address ?? '',
        metadata: {
          ...this._currentPatient?.metadata,
          address: data.address,
          moh: {
            ...this._currentPatient?.metadata?.moh,
            school: isMohSchool(data.school) ? data.school : undefined,
          },
        },
      });
      return;
    }
    this.patientDetailsChange.emit(undefined);
  }

  markAsTouched(): void {
    this.form.markAllAsTouched();
  }

  toReferralSource(
    referrer: WithRef<IReferralSourceConfiguration>
  ): IReferralSource {
    return ReferralSource.toReferrer(referrer);
  }

  isRequired$(field: PatientOptionalField): Observable<boolean> {
    return this.requiredFields$.pipe(
      map((requiredFields) => requiredFields.includes(field))
    );
  }

  schoolDisplayFn(school?: IMohSchool): string {
    return school ? school.schoolName : '';
  }

  protected _isUnder18(value: unknown): boolean {
    return isISODateType(value) && moment().diff(moment(value), 'years') < 18;
  }
}
